import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import Home from './pages/Home'
import Portfolio from './pages/Portfolio'
import Services from './pages/Services'
import About from './pages/About'
import Blog from './pages/Blog'
import Contact from './pages/Contact'
import PageTransition from './components/ui/PageTransition'
import ThemeSwitcher from './components/ui/ThemeSwitcher'
import { useTheme } from './hooks/useTheme'
import AdminLogin from './pages/admin/AdminLogin'
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminServices from './pages/admin/AdminServices'
import AdminPortfolio from './pages/admin/AdminPortfolio'
import AdminTestimonials from './pages/admin/AdminTestimonials'
import AdminTeam from './pages/admin/AdminTeam'
import AdminBlog from './pages/admin/AdminBlog'
import AdminStats from './pages/admin/AdminStats'
import AdminClients from './pages/admin/AdminClients'
import AdminContact from './pages/admin/AdminContact'
import AdminNavigation from './pages/admin/AdminNavigation'
import AdminMessages from './pages/admin/AdminMessages'

import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function ScrollProgress() {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1">
      <div className="absolute inset-0 bg-white/5" />
      <div
        className="relative h-full bg-gradient-to-r from-electric-blue via-electric-purple to-electric-blue bg-[length:200%_100%] animate-gradient-x transition-all duration-150 ease-out rounded-full"
        style={{ width: `${progress}%` }}
      >
        <div className="absolute inset-0 blur-md bg-gradient-to-r from-electric-blue via-electric-purple to-electric-blue opacity-60" />
      </div>
    </div>
  )
}

function BackToTop() {
  const [visible, setVisible] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      setVisible(scrollTop > 500)
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const radius = 18
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (scrollProgress / 100) * circumference

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 z-50 w-14 h-14 flex items-center justify-center cursor-pointer group"
          aria-label="Back to top"
        >
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
            <circle
              cx="24"
              cy="24"
              r={radius}
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="transition-all duration-300 ease-out"
            />
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'var(--electric-blue)' }} />
                <stop offset="100%" style={{ stopColor: 'var(--electric-purple)' }} />
              </linearGradient>
            </defs>
          </svg>
          <div className="relative w-10 h-10 rounded-full bg-gray-950 border border-white/10 flex items-center justify-center group-hover:border-electric-blue/50 transition-colors">
            <svg className="w-5 h-5 text-white group-hover:-translate-y-0.5 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  )
}

function ThemeFlash() {
  const { theme } = useTheme()
  const [flash, setFlash] = useState(false)
  const prevTheme = useRef(theme)

  useEffect(() => {
    if (prevTheme.current !== theme) {
      prevTheme.current = theme
      setFlash(true)
      const timer = setTimeout(() => setFlash(false), 400)
      return () => clearTimeout(timer)
    }
  }, [theme])

  return (
    <AnimatePresence>
      {flash && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.15 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] pointer-events-none"
          style={{
            background: `radial-gradient(circle at 50% 50%, var(--electric-blue), transparent 70%)`,
          }}
        />
      )}
    </AnimatePresence>
  )
}

const FONT_WEIGHTS = '300;400;500;600;700;800;900'
const GOOGLE_FONTS_BASE = 'https://fonts.googleapis.com/css2?family='

function loadGoogleFont(font: string) {
  if (font === 'Inter') return
  const id = `gf-${font.replace(/\s+/g, '-').toLowerCase()}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `${GOOGLE_FONTS_BASE}${font.replace(/ /g, '+')}:wght@${FONT_WEIGHTS}&display=swap`
  document.head.appendChild(link)
}

const API_URL = import.meta.env.VITE_API_URL || '/api'

function App() {
  const location = useLocation()
  useTheme()
  const isAdmin = location.pathname.startsWith('/admin')

  useEffect(() => {
    fetch(`${API_URL}/navigation`).then(r => r.json()).then(d => {
      const hf = d.heading_font || 'Inter'
      const bf = d.body_font || 'Inter'
      document.documentElement.style.setProperty('--font-heading', `"${hf}", "${bf}", Inter, system-ui, sans-serif`)
      document.documentElement.style.setProperty('--font-body', `"${bf}", Inter, system-ui, sans-serif`)
      loadGoogleFont(hf)
      if (bf !== hf) loadGoogleFont(bf)
    }).catch(() => {})
  }, [])

  if (isAdmin) {
    return (
      <Routes location={location} key={location.pathname}>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="portfolio" element={<AdminPortfolio />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="stats" element={<AdminStats />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="navigation" element={<AdminNavigation />} />
          <Route path="messages" element={<AdminMessages />} />
        </Route>
      </Routes>
    )
  }

  return (
    <>
      <ScrollToTop />
      <ScrollProgress />
      <ThemeFlash />
      <div className="min-h-screen bg-gray-950 text-white font-sans antialiased">
        <Navbar />
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageTransition><Home /></PageTransition>} />
            <Route path="/portfolio" element={<PageTransition><Portfolio /></PageTransition>} />
            <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
            <Route path="/about" element={<PageTransition><About /></PageTransition>} />
            <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
            <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          </Routes>
        </AnimatePresence>
        <Footer />
        <BackToTop />
        <ThemeSwitcher />
      </div>
    </>
  )
}

function AppWrapper() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  )
}

export default AppWrapper
